import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-4">该文章不存在或已被移除</p>
            <Link to="/" onClick={scrollToTop} className="text-sm text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  function formatViews(views: number): string {
    if (views >= 10000) return (views / 10000).toFixed(1) + '万';
    return views.toString();
  }

  return (
    <>
      <Navbar />
      <main>
        <article className="pt-20">
          <header className="max-w-3xl mx-auto px-4 md:px-6 py-8">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6">
              <Link to="/" onClick={scrollToTop} className="hover:text-primary-500 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${article.category}`} onClick={scrollToTop} className="hover:text-primary-500 transition-colors">
                {article.categoryName}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600 truncate max-w-[200px]">{article.title}</span>
            </nav>

            <div className="flex flex-wrap gap-2 mb-4">
              <span className="px-2.5 py-1 text-xs font-medium bg-accent-500 text-background-50 rounded-md">
                {article.categoryName}
              </span>
              {article.isFeatured && (
                <span className="px-2.5 py-1 text-xs font-medium bg-primary-500 text-background-50 rounded-md">
                  独家
                </span>
              )}
              {article.isHot && (
                <span className="px-2.5 py-1 text-xs font-medium bg-secondary-500 text-background-50 rounded-md">
                  热门
                </span>
              )}
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>

            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              {article.excerpt}
            </p>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400 pb-6 border-b border-background-200/70">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span>{article.publishDate}</span>
              <span>{article.readTime} 分钟阅读</span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatViews(article.views)} 阅读
              </span>
            </div>
          </header>

          <div className="max-w-3xl mx-auto px-4 md:px-6 pb-12">
            <div className="relative w-full h-[360px] md:h-[480px] rounded-lg overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed
                [&_p]:mb-5 [&_p]:leading-relaxed
                [&_strong]:text-foreground-950 [&_strong]:font-semibold
                [&_h2]:text-xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4
                [&_h3]:text-lg [&_h3]:font-heading [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-3"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full border border-background-200"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 bg-background-100/60">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard
                    key={ra.id}
                    slug={ra.slug}
                    title={ra.title}
                    excerpt={ra.excerpt}
                    coverImage={ra.coverImage}
                    categoryName={ra.categoryName}
                    categorySlug={ra.category}
                    author={ra.author}
                    publishDate={ra.publishDate}
                    readTime={ra.readTime}
                    views={ra.views}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Organization',
              name: article.author,
            },
            datePublished: article.publishDate,
            image: article.coverImage,
            articleSection: article.categoryName,
            inLanguage: 'zh-CN',
            publisher: {
              '@type': 'Organization',
              name: '911黑料',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://yrkcpte.cn/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.categoryName,
                item: `https://yrkcpte.cn/category/${article.category}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
                item: `https://yrkcpte.cn/article/${article.slug}`,
              },
            ],
          }),
        }}
      />
    </>
  );
}